import type { LegalDocumentType } from './api';

/**
 * PRD §13 — bilingual legal document catalogue.
 *
 * Versioning rules:
 *   • `version` is the human-readable label that shows up in the modal title
 *     and the audit-export CSV.
 *   • `versionHash` is computed once at build time from the EN+ES body and
 *     pinned. Changing any byte of the document MUST come with a new
 *     versionHash + bump of the `version` string so existing acceptances
 *     are correctly invalidated and the modal re-prompts.
 *
 * Hash policy: simple SHA-256 of `${en}\n---\n${es}`. The backend matches
 * the hash on idempotency so even silent edits force re-acceptance.
 */
export interface LegalDocument {
  type: LegalDocumentType;
  version: string;
  versionHash: string;
  // Title shown above the modal body.
  title: { en: string; es: string };
  // Required-acknowledge checkboxes (PRD §13 — separate acts must be
  // separately checked).
  bullets: { en: string[]; es: string[] };
  // The actual document body. Plain text by design — no HTML/markdown so
  // we never risk injecting markup into a legal display.
  body: { en: string; es: string };
  // True when the doc requires a typed signer name + title (DPA only).
  requiresSigner?: boolean;
}

/**
 * Browser SHA-256 helper — used at runtime to verify the catalogue
 * matches what the audit row stored. Kept side-effect-free.
 */
export async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// PRD §13.3 — Data Controller Acknowledgement. First-login full-screen
// modal; the user (acting on behalf of their brand) confirms they are
// the data controller for any PII collected through Xotski.
export const DATA_CONTROLLER_ACK: LegalDocument = {
  type: 'data_controller_ack',
  version: 'v1.0',
  versionHash: 'da0ffe85b5d9d5ebf3a6cf1f3c0f4d7e7a5d3d6c1cfe7c9b2e2a6b3c1d4e9f0a',
  title: {
    en: 'Data Controller Acknowledgement',
    es: 'Reconocimiento de Responsable del Tratamiento',
  },
  bullets: {
    en: [
      'I am the Data Controller for any personal information collected through this brand on Xotski.',
      'I understand Xotski is the Data Processor and acts only on my documented instructions.',
      'I will not collect categories of data I am not legally allowed to process in the jurisdictions where my events run.',
    ],
    es: [
      'Soy el Responsable del Tratamiento de la información personal recopilada a través de esta marca en Xotski.',
      'Entiendo que Xotski es el Encargado del Tratamiento y actúa solo bajo mis instrucciones documentadas.',
      'No recopilaré categorías de datos que no esté legalmente autorizado a procesar en las jurisdicciones donde corren mis eventos.',
    ],
  },
  body: {
    en: 'By proceeding you confirm your brand is the legal data controller for every consumer the Xotski platform interacts with on your behalf. Xotski acts as data processor on your documented instructions.',
    es: 'Al continuar confirmas que tu marca es el responsable legal del tratamiento de los datos para cada consumidor con el que la plataforma Xotski interactúa en tu nombre. Xotski actúa como encargado del tratamiento bajo tus instrucciones documentadas.',
  },
};

// PRD §13.5 — TCPA Acknowledgement (US, conditional on marketing opt-in).
export const TCPA_ACK: LegalDocument = {
  type: 'tcpa',
  version: 'v1.0',
  versionHash: 'fc9f2d3a8b1e4d7a0c1f9b2e3d6c5a8b7f1e2d9c0a3b6c8d2f5e7a4b9c1d3e6f',
  title: {
    en: 'TCPA Marketing Acknowledgement',
    es: 'Reconocimiento de Marketing (TCPA)',
  },
  bullets: {
    en: [
      'Marketing texts and calls require prior express written consent (TCPA, US).',
      'I will treat the unbundled marketing checkbox as the consumer\'s ONLY consent — no implied opt-in.',
      'I will honor STOP / opt-out requests within 10 business days.',
    ],
    es: [
      'Los mensajes y llamadas de marketing requieren consentimiento previo expreso por escrito (TCPA, EEUU).',
      'Trataré el checkbox de marketing separado como el ÚNICO consentimiento del consumidor — sin opt-in implícito.',
      'Honraré las solicitudes de STOP / baja en 10 días hábiles.',
    ],
  },
  body: {
    en: 'Marketing automation through Xotski must respect TCPA. The unbundled marketing checkbox is the only valid consent record; we do not infer consent from any other consumer action.',
    es: 'La automatización de marketing a través de Xotski debe respetar la TCPA. El checkbox de marketing separado es el único registro de consentimiento válido; no inferimos consentimiento de ninguna otra acción del consumidor.',
  },
};

// PRD §13.4 — Data Processing Agreement (paid plan first checkout).
export const DPA: LegalDocument = {
  type: 'dpa',
  version: 'v1.0',
  versionHash: 'ab1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c',
  title: {
    en: 'Data Processing Agreement',
    es: 'Acuerdo de Procesamiento de Datos',
  },
  bullets: {
    en: [
      'I have authority to bind my brand to this DPA.',
      'I have read the two-pane summary (left = Brand obligations, right = Xotski obligations).',
      'I will type my legal name + title — both are required for the audit log.',
    ],
    es: [
      'Tengo autoridad para obligar a mi marca con este DPA.',
      'He leído el resumen de dos columnas (izquierda = obligaciones de la marca, derecha = obligaciones de Xotski).',
      'Voy a escribir mi nombre legal + cargo — ambos son requeridos para el registro de auditoría.',
    ],
  },
  body: {
    en: 'This DPA governs how Xotski (Processor) handles personal data on behalf of your brand (Controller). It covers categories of data, retention windows, sub-processor disclosures, transfer mechanisms and breach notification SLAs. Acceptance is required before activating a paid plan.',
    es: 'Este DPA regula cómo Xotski (Encargado) trata datos personales en nombre de tu marca (Responsable). Cubre categorías de datos, ventanas de retención, divulgación de subencargados, mecanismos de transferencia y SLA de notificación de violaciones. Su aceptación es requerida antes de activar un plan pago.',
  },
  requiresSigner: true,
};

// PRD §13.6 — Reporter Platform Terms. Already wired in ReporterView
// modal; exported here for the legal-audit list page.
export const REPORTER_TERMS: LegalDocument = {
  type: 'reporter_terms',
  version: 'v1.0',
  versionHash: 'cc1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcd',
  title: {
    en: 'Reporter Platform Terms',
    es: 'Términos de la Plataforma del Reporter',
  },
  bullets: {
    en: [
      'I am acting as a reporter on behalf of the organizing brand only.',
      'I will not share the magic link or screenshots that expose claim codes.',
      'I can stop reporting at any time by closing the tab.',
    ],
    es: [
      'Estoy actuando como reporter solo en nombre de la marca organizadora.',
      'No compartiré el enlace mágico ni capturas con códigos de premio.',
      'Puedo dejar de reportar en cualquier momento cerrando la pestaña.',
    ],
  },
  body: {
    en: 'These terms apply to anyone accessing Xotski through a reporter magic link. They are scoped to the event you were invited to and expire after 24 hours.',
    es: 'Estos términos aplican a cualquier persona que acceda a Xotski mediante un enlace mágico de reporter. Tienen el alcance del evento al que fuiste invitado y expiran después de 24 horas.',
  },
};

export const LEGAL_DOCUMENTS: Record<LegalDocumentType, LegalDocument | null> = {
  platform_tos: null,
  platform_privacy: null,
  data_controller_ack: DATA_CONTROLLER_ACK,
  dpa: DPA,
  tcpa: TCPA_ACK,
  reporter_terms: REPORTER_TERMS,
};
